import { MedicationsPermissions } from '../MedicationsPermissions';
import { IUnprocessableEntity } from '../Shared/HttpRequestExecutor';
import { Nullable } from '../Shared/Types';

export function isUnprocessableInvalidGroups(entity: IUnprocessableEntity): entity is IUnprocessableInvalidGroups {
    return (entity as IUnprocessableInvalidGroups).content !== undefined;
}

export interface IInvalidGroup {
    id: string;
    name: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface IUnprocessableInvalidGroups extends IUnprocessableEntity {
    content: IInvalidGroup[];
}

export enum AuthState {
    Allowed = 'allowed',
    Denied = 'denied'
}

export interface PermissionsIPermission {
    definitionCode: MedicationsPermissions;
    state: AuthState;
}

export interface UserIUser {
    id: string;
    familyName: string;
    givenNames: string;
    username: string;
    email: string;
    enabled: boolean;
    isSystemContact: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface UserICreateNewUser {
    familyName: string;
    givenNames: string;
    email: string;
    username: string;
    enabled: boolean;
    isSystemContact: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface UserIEditUser {
    familyName: string;
    givenNames: string;
    email: string;
    username: string;

    enabled: boolean;
    isSystemContact: boolean;
}

export interface UserIUserPermissions {
    user: UserIUserDefinition;
    modules: UserIModule[];
}

export interface UserIEditParentGroups {
    permissionOwnerId: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    groups: UserIParentGroup[];
}

export interface UserIParentGroup {
    id: string;
    name: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface UserIUserDefinition {
    userId: string;
    name: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface UserIModule {
    moduleCode: string;
    name: string;
    description: string;
    permissions: UserIPermission[];
}

export interface UserIPermission {
    definitionCode: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    name: string;
    description: string;
    state: AuthState;
    inherited: boolean;
    inherritablePermission: Nullable<UserInherritablePermission>;
}

export interface UserInherritablePermission {
    groupId: string;
    name: string;
    state: AuthState;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface UserIReplaceUserPermissions {
    modules: UserIModuleUpdate[];
}

export interface UserIModuleUpdate {
    moduleCode: string;
    permissions: UserIPermissionUpdate[];
}

export interface UserIPermissionUpdate {
    definitionCode: string;
    state: AuthState;
    inherited: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface GroupICreateNewGroup {
    username: string;
    name: string;
    enabled: boolean;
    isDefaultGroup: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface GroupIEditGroup {
    username: string;
    name: string;
    enabled: boolean;
    isDefaultGroup: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface GroupIGroup {
    id: string;
    username: string;
    name: string;
    enabled: boolean;
    isDefaultGroup: boolean;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    parentGroups: GroupIParentGroup[];
    members: GroupIGroupMember[];
}

export interface GroupIParentGroup {
    id: string;
    name: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface GroupIGroupMember {
    id: string;
    name?: string;
    username: string;
    familyName?: string;
    givenNames?: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    type: GroupMemberType;
}

export interface GroupIEditGroupMember {
    id: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

export interface GroupIReplaceMembersInGroup {
    groupId: string;
    members: GroupIEditGroupMember[];
}

export enum GroupMemberType {
    User = 'user',
    Group = 'group'
}

export interface GroupIGroupPermissions {
    user: GroupIGroupDefinition;
    modules: GroupIModule[];
}

export interface GroupIGroupDefinition {
    groupId: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    state: AuthState;
    name: string;
}

export interface GroupIModule {
    moduleCode: string;
    name: string;
    description: string;
    permissions: GroupIPermission[];
}

export interface GroupIPermission {
    definitionCode: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    name: string;
    description: string;
    state: AuthState;
    inherited: boolean;
    inherritablePermission: Nullable<GroupInherritablePermission>;
}

export interface GroupInherritablePermission {
    groupId: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    state: AuthState;
    name: string;
}

export interface GroupIGroupPermissionsUpdate {
    modules: GroupIModuleUpdate[];
}

export interface GroupIModuleUpdate {
    moduleCode: string;
    permissions: GroupIPermissionUpdate[];
}

export interface GroupIPermissionUpdate {
    definitionCode: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    state: AuthState;
    inherited: boolean;
}

export interface GroupIEditParentGroups {
    permissionOwnerId: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    groups: GroupIParentGroup[];
}
